import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { FalseAlarmList, MisclassificationDetail } from '@/components/testlab/FalseAlarms';
import { ValidationMetrics, SignalAnalysis, SplitGuard, Experiments } from '@/components/testlab/AnalysisPanels';

const API = process.env.REACT_APP_BACKEND_URL;

export const useAnalysisApi = (headers) => {
  const auth = headers.Authorization;
  return useCallback(async (path, opts = {}) => {
    const r = await fetch(`${API}/api/test-lab/analysis/${path}`, { headers: { Authorization: auth, 'Content-Type': 'application/json' }, ...opts });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.detail || 'Fel');
    return data;
  }, [auth]);
};

export default function TestLabAnalysis({ headers, onBack }) {
  const api = useAnalysisApi(headers);
  const [falseAlarms, setFalseAlarms] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => { api('false-alarms').then(setFalseAlarms).catch(e => setFalseAlarms({ rows: [], error: e.message })); }, [api]);

  return (
    <div className="space-y-4" data-testid="testlab-analysis">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="analysis-back-btn">&larr; Tillbaka</Button>
      <div>
        <h2 className="text-lg font-bold tracking-tight flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500" /> Falsklarm &amp; felklassificeringar
        </h2>
        <p className="text-xs text-muted-foreground">Facit jämförs med produktionssystemets loggade beslut. Inga mätningar eller beslut ändras.</p>
      </div>
      <Tabs defaultValue="false-alarms">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="false-alarms" data-testid="tab-false-alarms">Felklassificeringar</TabsTrigger>
          <TabsTrigger value="metrics" data-testid="tab-metrics">Statistik</TabsTrigger>
          <TabsTrigger value="signals" data-testid="tab-signals">Signalanalys</TabsTrigger>
          <TabsTrigger value="splits" data-testid="tab-splits">Datagrupper</TabsTrigger>
          <TabsTrigger value="experiments" data-testid="tab-experiments">Experiment</TabsTrigger>
        </TabsList>
        <TabsContent value="false-alarms">
          {falseAlarms ? <FalseAlarmList data={falseAlarms} onOpen={setOpenId} /> : <Loader2 className="h-5 w-5 animate-spin" />}
        </TabsContent>
        <TabsContent value="metrics"><ValidationMetrics api={api} onOpen={setOpenId} /></TabsContent>
        <TabsContent value="signals"><SignalAnalysis api={api} /></TabsContent>
        <TabsContent value="splits"><SplitGuard api={api} /></TabsContent>
        <TabsContent value="experiments"><Experiments api={api} /></TabsContent>
      </Tabs>
      {openId && <MisclassificationDetail api={api} id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
